/**
 * SubClock — PUBLIC page at /subclock (subcontractor foremen, no CRM login).
 * Foreman picks company + PIN → Check In / Check Out on a job with GPS capture.
 * Attendance only — completely separate from the crew payroll time clock.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { HardHat, LogIn, LogOut, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const F = "w-full h-11 px-3 border border-zinc-300 bg-white rounded-sm text-sm";
const L = "block text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-700 mb-1";

const getGps = () => new Promise((res) => {
  if (!navigator.geolocation) return res(null);
  navigator.geolocation.getCurrentPosition(
    (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => res(null), { timeout: 8000 }
  );
});

const mt = (ts) => ts ? new Date(ts).toLocaleTimeString("en-US", { timeZone: "America/Denver", hour: "numeric", minute: "2-digit" }) : "—";

export default function SubClock() {
  const [boot, setBoot] = useState({ companies: [], jobs: [] });
  const [vendorId, setVendorId] = useState(localStorage.getItem("sc_vendor") || "");
  const [foreman, setForeman] = useState(localStorage.getItem("sc_foreman") || "");
  const [pin, setPin] = useState("");
  const [st, setSt] = useState(null);
  const [dealId, setDealId] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { axios.get(`${API}/public/subclock/bootstrap`).then((r) => setBoot(r.data)).catch(() => {}); }, []);

  const creds = () => ({ vendor_id: vendorId, pin });
  const refresh = async () => {
    const r = await axios.post(`${API}/public/subclock/status`, creds());
    setSt(r.data);
  };
  const login = async () => {
    setErr(""); setBusy("login");
    try {
      await refresh();
      localStorage.setItem("sc_vendor", vendorId);
      localStorage.setItem("sc_foreman", foreman);
    } catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(""); }
  };

  const doPunch = async (action) => {
    setErr(""); setBusy(action);
    try {
      const gps = await getGps();
      await axios.post(`${API}/public/subclock/punch`, {
        ...creds(), action, deal_id: dealId, foreman_name: foreman,
        lat: gps?.lat, lng: gps?.lng,
      });
      localStorage.setItem("sc_foreman", foreman);
      await refresh();
    } catch (e) { setErr(e?.response?.data?.detail || "Punch failed"); }
    finally { setBusy(""); }
  };

  const open = st?.open_punch;

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center px-4 py-8" data-testid="subclock-page">
      <img src="/sealtech-logo.png" alt="SealTech" className="h-12 w-auto mb-4 bg-white rounded-sm p-1.5" />
      <div className="w-full max-w-md bg-white rounded-sm shadow-2xl overflow-hidden">
        <div className="px-6 py-4 bg-blue-700 text-white flex items-center gap-2">
          <HardHat className="w-5 h-5" />
          <div>
            <h1 className="font-heading text-xl font-black tracking-tight">SealTech Sub Check-In</h1>
            <p className="text-[11px] text-blue-100">Check in when your crew arrives, out when you leave.</p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {err && <div className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="sc-error">{err}</div>}
          {!st ? (
            <>
              <div>
                <label className={L}>Your company</label>
                <select data-testid="sc-company" className={F} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                  <option value="">Select your company…</option>
                  {boot.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={L}>Your name (foreman / lead)</label>
                <input data-testid="sc-foreman" className={F} value={foreman} onChange={(e) => setForeman(e.target.value)} placeholder="First & last name" />
              </div>
              <div>
                <label className={L}>PIN</label>
                <input data-testid="sc-pin" type="password" inputMode="numeric" className={F} value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" />
              </div>
              <button data-testid="sc-login" disabled={!vendorId || !pin || !foreman.trim() || busy === "login"} onClick={login}
                className="w-full h-12 bg-zinc-950 text-white text-sm font-bold uppercase tracking-wider rounded-sm disabled:opacity-50">
                {busy === "login" ? "Checking…" : "Continue"}
              </button>
            </>
          ) : (
            <>
              <div className="text-xs text-zinc-500 -mb-1">{st.company} · {foreman}</div>
              {open ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-sm" data-testid="sc-open-punch">
                  <div className="font-bold text-emerald-800">Checked in — {open.deal_title}</div>
                  <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> since {mt(open.check_in)} MT
                    {open.on_site_in === true && " · ✓ on site"}
                    {open.on_site_in === false && ` · ${open.distance_in_mi} mi from site`}
                  </div>
                </div>
              ) : (
                <div>
                  <label className={L}>Which job?</label>
                  <select data-testid="sc-job" className={F} value={dealId} onChange={(e) => setDealId(e.target.value)}>
                    <option value="">Select the job…</option>
                    {boot.jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                </div>
              )}
              {!open ? (
                <button data-testid="sc-checkin" disabled={!dealId || busy === "in"} onClick={() => doPunch("in")}
                  className="w-full h-14 bg-emerald-600 text-white text-base font-black uppercase tracking-wider rounded-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  <LogIn className="w-5 h-5" /> {busy === "in" ? "Checking in…" : "Check In"}
                </button>
              ) : (
                <button data-testid="sc-checkout" disabled={busy === "out"} onClick={() => doPunch("out")}
                  className="w-full h-14 bg-red-600 text-white text-base font-black uppercase tracking-wider rounded-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  <LogOut className="w-5 h-5" /> {busy === "out" ? "Checking out…" : "Check Out"}
                </button>
              )}
              <div className="text-[10px] text-zinc-400 text-center">GPS is recorded with each check-in/out.</div>
              {(st.today || []).length > 0 && (
                <div className="border-t border-zinc-100 pt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Today</div>
                  {st.today.map((t) => (
                    <div key={t.id} className="text-xs text-zinc-700 py-0.5">
                      {t.deal_title}: {mt(t.check_in)} → {t.check_out ? `${mt(t.check_out)} (${t.hours}h)` : "on site"}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
